import { MenuItem } from "@/redux/services/menu/menu.type";
import {
  displayPrice,
  getImageUrl,
  getMenuItemDisplayPrice,
} from "@/lib/utils";
import { useAppSelector } from "@/redux/features/hooks";
import { useMemo } from "react";
import { useRestaurantCurrency } from "@/lib/hooks";
import Switch from "@/components/ui/switch";
import { useChangeMenuItemAvailabilityMutation } from "@/redux/services/menu/menu.api";
import toast from "react-hot-toast";

interface Props {
  menuItem: MenuItem;
  openMenuItemModal: (menuItem: MenuItem) => void;
  isReadOnly?: boolean;
}

const MenuItemCard = ({ menuItem, openMenuItemModal, isReadOnly }: Props) => {
  const currentRestaurant = useAppSelector(
    (state) => state.user.currentRestaurant
  );
  const currency = useRestaurantCurrency();

  const [changeAvailability, { isLoading: isUpdatingAvailability }] =
    useChangeMenuItemAvailabilityMutation();

  const displayedPrice = useMemo(() => {
    return getMenuItemDisplayPrice(menuItem);
  }, [menuItem]);

  const handleAvailabilityChange = async () => {
    const res = await changeAvailability({
      id: menuItem.id,
      isAvailable: !menuItem.isAvailable,
    });
    if ("data" in res) {
      toast.success(
        `Élément ${res.data?.isAvailable ? "disponible" : "indisponible"} !`
      );
    } else {
      toast.error("Une erreur s'est produite !");
    }
  };

  return (
    <div
      className={`flex flex-col items-center bg-card border border-border p-4 rounded-lg cursor-pointer hover:shadow-md transition-all relative ${
        !menuItem.isAvailable ? "opacity-75" : ""
      }`}
      onClick={() => openMenuItemModal(menuItem)}
    >
      <div className="w-24 h-24 sm:w-28 sm:h-28 overflow-hidden rounded-full border-2 border-border mb-3 relative">
        <img
          src={getImageUrl(
            menuItem.product?.image?.url ??
              menuItem.image?.url ??
              currentRestaurant?.image?.url
          )}
          alt={menuItem.product?.title ?? menuItem.title}
          className={`w-full h-full object-cover ${
            !menuItem.isAvailable ? "grayscale" : ""
          }`}
        />
        {!menuItem.isAvailable && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-full">
            <span className="text-white text-xs font-medium px-2 py-0.5 bg-destructive rounded">
              Indisponible
            </span>
          </div>
        )}
      </div>

      <h3 className="font-semibold text-center text-card-foreground text-sm mb-1">
        {menuItem.product?.title ?? menuItem.title}
      </h3>

      <div className="text-primary font-bold text-base">
        {displayPrice(displayedPrice, currency)}
      </div>

      {!isReadOnly && (
        // Stop propagation so toggling availability never opens the details drawer.
        <div
          className="flex items-center gap-2 mt-3"
          onClick={(e) => e.stopPropagation()}
        >
          <Switch
            id={`menu-item-availability-${menuItem.id}`}
            checked={menuItem.isAvailable}
            onChange={handleAvailabilityChange}
            disabled={isUpdatingAvailability}
          />
          <span className="text-xs text-muted-foreground">
            {isUpdatingAvailability
              ? "Mise à jour..."
              : menuItem.isAvailable
                ? "Disponible"
                : "Indisponible"}
          </span>
        </div>
      )}
    </div>
  );
};

export const MenuItemCardSkeleton = () => {
  return (
    <div className="flex flex-col items-center bg-card border border-border p-4 rounded-lg animate-pulse">
      <div className="w-24 h-24 sm:w-28 sm:h-28 bg-gray-200 rounded-full mb-3" />
      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
      <div className="h-5 bg-gray-200 rounded w-1/2" />
    </div>
  );
};

export default MenuItemCard;
